import React, {useMemo, useRef} from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import {useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import {Roles} from "@evennt-tech/api-v2-core";
import Alert from "view/components/Alert";
import {selectUser} from "store/auth/selectors";
import {useRichTextImages} from "view/components/RichText/hooks/useRichTextImages";
import {
  stripPersistedExhibitorIframeNotices,
  stripPersistedIframeStatusNotices,
} from "view/components/RichText/tools/iframeStatusNotices";
import {sanitizeHtml} from "view/components/SafeHtml";
import {scopeShowInfoLinks} from "view/components/RichText/tools/scopeShowInfoLinks";
import useStyles from "./styles";
import useRedirectToExternalLink from "./hooks/useRedirectToExternalLink";
import {useFilterUnverifiedIframes} from "./hooks/useFilterUnverifiedIframes";
import {useReadonlyIframeStatus} from "./hooks/useReadonlyIframeStatus";
import {useEmbedFocusScrollGuard} from "./hooks/useEmbedFocusScrollGuard";

type RichTextProps = {
  content?: string;
  className?: string;
};

const RichText: React.FC<RichTextProps> = ({content, className = ""}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const {role} = useSelector(selectUser);
  const {showId, exhibitorPairId} = useParams();
  const renderedContent = useMemo(() => {
    if (!content) {
      return content;
    }

    const withNoticesStripped = (() => {
      if (role === Roles.EXHIBITOR) {
        const exhibitorContent = stripPersistedExhibitorIframeNotices(content);
        return showId && exhibitorPairId
          ? scopeShowInfoLinks(exhibitorContent, {showId, exhibitorPairId})
          : exhibitorContent;
      }

      if (role === Roles.ORGANISER) {
        return stripPersistedIframeStatusNotices(content);
      }

      return content;
    })();

    // mXSS defence-in-depth — see SafeHtml's comment.
    return sanitizeHtml(withNoticesStripped);
  }, [content, role, showId, exhibitorPairId]);

  useRedirectToExternalLink(containerRef, renderedContent);
  useRichTextImages();
  useFilterUnverifiedIframes(containerRef, renderedContent);
  const overlayEntries = useReadonlyIframeStatus(containerRef, renderedContent);
  useEmbedFocusScrollGuard(containerRef, renderedContent);

  if (!renderedContent) {
    return null;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(classes.root, className)}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: renderedContent,
        }}
      />
      {overlayEntries.map((entry, index) =>
        ReactDOM.createPortal(
          <div
            key={`${entry.status}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 16px",
              pointerEvents: "none",
              backgroundColor: "#ffffff",
            }}
          >
            <Alert
              severity={
                entry.status === CheckVerifiedDomainStatus.REJECTED
                  ? "error"
                  : "warning"
              }
              fullWidth
            >
              {entry.status === CheckVerifiedDomainStatus.REJECTED
                ? "Sorry, the link was rejected. The IFrame is not visible to Exhibitors. Please contact support for more details."
                : "Waiting for Approval. Domain sent to FFAIR Team for verification. The IFrame is not visible to Exhibitors. Please contact support for more details."}
            </Alert>
          </div>,
          entry.wrap
        )
      )}
    </>
  );
};

export default React.memo(RichText);
